import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Phone, TrendingUp, Clock, MessageCircle, Sparkles, MapPin, Play } from "lucide-react";
import { Link } from "react-router-dom";
import AudioPlayerModal from "@/components/AudioPlayerModal";

// Slide data configuration
const slides = [
  {
    id: 'virtual-host',
    headline: 'AI Phone Ordering That Never Sleeps',
    subheadline: 'Your AI host answers every call, takes orders, and never puts customers on hold.',
    image: '/images/hero/virtual-host.png',
    primaryCTA: { text: 'Sign Up Now', href: '/restaurant-menu-search' },
    secondaryCTA: { text: 'Play Demo', href: '#how-it-works', style: 'demo' },
    metrics: [
      { icon: Phone, value: 'Never Miss', label: 'a Call' },
      { icon: TrendingUp, value: '+25%', label: 'Order Volume' }
    ],
    imageSize: 'small' // More breathing room, positioned to the right
  },
  {
    id: 'virtual-assistant',
    headline: 'Your AI-Powered Virtual Assistant',
    subheadline: 'Customers get instant answers about ingredients, allergens, and recommendations.',
    image: '/images/hero/virtual-assistant.png',
    primaryCTA: { text: 'Start Free Trial', href: '/restaurant-menu-search' },
    secondaryCTA: { text: 'Learn More', href: '/restaurants', style: 'demo' },
    metrics: [
      { icon: Clock, value: '24/7', label: 'Availability' },
      { icon: MessageCircle, value: '90%', label: 'Questions Answered' }
    ]
  },
  {
    id: 'customer-app',
    headline: 'Discover Your Perfect Dish',
    subheadline: 'Personalized recommendations, dietary filters, and easy ordering from any restaurant.',
    image: '/images/hero/customer-app.png',
    primaryCTA: { text: 'Download the App', href: '/customers' },
    secondaryCTA: { text: 'For Customers', href: '/customers', style: 'demo' },
    metrics: [
      { icon: Sparkles, value: 'Smart', label: 'Recommendations' },
      { icon: MapPin, value: '500+', label: 'Restaurants' }
    ],
    imageSize: 'small' // This image needs to be smaller to avoid overlap
  }
];

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAudioModalOpen, setIsAudioModalOpen] = useState(false);

  // Auto-cycle every 10 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % slides.length);
    }, 10000);
    return () => clearInterval(timer);
  }, []);

  const currentSlideData = slides[currentSlide];

  return (
    <section className="relative bg-[#2256e5] overflow-hidden">
      {/* Background blur effects */}
      <div className="absolute inset-0 pointer-events-none opacity-15">
        <div className="absolute top-20 left-10 w-40 h-40 rounded-full bg-dineai-yellow/30 blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-60 h-60 rounded-full bg-white/10 blur-3xl"></div>
      </div>

      {/* Content - CSS Grid for precise control */}
      <div className="relative z-10 container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-4 min-h-auto lg:min-h-[650px]">

          {/* Left Column - Text Content */}
          <div className="flex flex-col justify-center pt-8 pb-4 lg:py-16 relative z-10">
            <AnimatePresence mode="popLayout">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: 0.8,
                  ease: "easeInOut"
                }}
              >
                {/* Headline */}
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-5">
                  <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">
                    {currentSlideData.headline}
                  </span>
                </h1>

                {/* Subheadline */}
                <p className="text-white/90 text-base sm:text-lg md:text-xl mb-6 leading-relaxed max-w-lg">
                  {currentSlideData.subheadline}
                </p>

                {/* Metrics Bar */}
                <div className="flex flex-wrap gap-6 mb-8">
                  {currentSlideData.metrics.map((metric, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20">
                        <metric.icon className="w-5 h-5 text-dineai-yellow" />
                      </div>
                      <div className="text-left">
                        <div className="text-white font-bold text-lg">{metric.value}</div>
                        <div className="text-white/70 text-xs">{metric.label}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to={currentSlideData.primaryCTA.href}>
                    <Button className="bg-dineai-yellow text-black hover:bg-dineai-yellow/90 py-6 px-8 text-lg font-bold rounded-full shadow-lg transform hover:scale-105 transition-all flex items-center gap-2">
                      {currentSlideData.primaryCTA.text}
                      <ArrowRight className="w-5 h-5" />
                    </Button>
                  </Link>
                  {/* Handle Play Demo button for Virtual Host slide */}
                  {currentSlideData.id === 'virtual-host' ? (
                    <Button
                      onClick={() => setIsAudioModalOpen(true)}
                      className="py-6 px-8 text-lg font-medium rounded-full transform hover:scale-105 transition-all bg-transparent border-2 border-[#43acff] text-[#43acff] hover:bg-[#43acff] hover:text-white flex items-center gap-2"
                    >
                      <Play className="w-5 h-5" />
                      {currentSlideData.secondaryCTA.text}
                    </Button>
                  ) : currentSlideData.secondaryCTA.href.startsWith('#') ? (
                    <a href={currentSlideData.secondaryCTA.href}>
                      <Button className={`py-6 px-8 text-lg font-medium rounded-full transform hover:scale-105 transition-all ${
                        currentSlideData.secondaryCTA.style === 'demo'
                          ? 'bg-transparent border-2 border-[#43acff] text-[#43acff] hover:bg-[#43acff] hover:text-white'
                          : 'bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white hover:bg-white/20 hover:border-white/50'
                      }`}>
                        {currentSlideData.secondaryCTA.text}
                        {currentSlideData.secondaryCTA.style === 'demo' && <ArrowRight className="w-5 h-5 ml-2" />}
                      </Button>
                    </a>
                  ) : (
                    <Link to={currentSlideData.secondaryCTA.href}>
                      <Button className={`py-6 px-8 text-lg font-medium rounded-full transform hover:scale-105 transition-all ${
                        currentSlideData.secondaryCTA.style === 'demo'
                          ? 'bg-transparent border-2 border-[#43acff] text-[#43acff] hover:bg-[#43acff] hover:text-white'
                          : 'bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white hover:bg-white/20 hover:border-white/50'
                      }`}>
                        {currentSlideData.secondaryCTA.text}
                        {currentSlideData.secondaryCTA.style === 'demo' && <ArrowRight className="w-5 h-5 ml-2" />}
                      </Button>
                    </Link>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Right Column - Image with fixed height container */}
          {/* Mobile: relative positioning below text, Desktop: absolute with overflow */}
          <div className="relative lg:overflow-visible h-[300px] sm:h-[350px] lg:h-auto overflow-hidden lg:overflow-visible">
            <AnimatePresence mode="popLayout">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.02 }}
                transition={{
                  duration: 0.8,
                  ease: "easeInOut"
                }}
                className={`
                  lg:absolute
                  ${currentSlideData.imageSize === 'small'
                    ? 'h-[280px] sm:h-[320px] lg:h-[600px] lg:-right-16 xl:-right-20 lg:-bottom-[65px]'
                    : 'h-[280px] sm:h-[320px] lg:h-[680px] lg:-right-32 xl:-right-44 lg:-bottom-[75px]'
                  }
                  flex justify-center lg:block
                `}
              >
                <img
                  src={currentSlideData.image}
                  alt={currentSlideData.headline}
                  className="h-full w-auto max-w-none object-contain object-bottom"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="flex justify-center gap-3 py-6 relative z-20">
          {slides.map((slide, index) => (
            <button
              key={slide.id}
              onClick={() => setCurrentSlide(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? 'bg-dineai-yellow w-8'
                  : 'bg-white/40 hover:bg-white/60 w-2'
              }`}
              aria-label={`Go to slide ${index + 1}: ${slide.id}`}
            />
          ))}
        </div>
      </div>

      {/* Audio Player Modal for Virtual Host Demo */}
      <AudioPlayerModal
        isOpen={isAudioModalOpen}
        onClose={() => setIsAudioModalOpen(false)}
        audioSrc="/audio/virtual-host-demo.wav"
        title="AI Virtual Host Demo"
      />
    </section>
  );
};

export default Hero;
